import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { formatCurrency } from '../../../../components';
import {
  Feature,
  getProductId,
  PlanInterval,
  SubscriptionSource,
} from '../../../../models';
import {
  fromOrder,
  fromPromotion,
  fromSubscription,
  useAppDispatch,
} from '../../../../store';
import {
  DEFAULT_CURRENCY_FORMAT_OPTIONS,
  PLAN_SUBSCRIPTION_ID_QUERY_PARAM,
} from '../../../../utils/Constants';
import {
  getCycleFromPathParam,
  getQuotaFromPathParam,
  sendTrackingData,
  TrackingPayload,
} from '../../../../utils/utils';
import {
  getDiscountPrice,
  getUnitPrice,
  useGetTrackingCookies,
  usePlanCartSnapshot,
} from '../../../../hook';
import { useLaunchDarkly } from '../../../../launchdarkly';
import { useQuery } from '../../../../routes';
import {
  getTargetSelfServeSubscription,
  isPaidRecurlySubscription,
  isSelfServePaidSubscription,
  parseSubscriptionId,
} from '../../../../utils/subscriptionTarget';
import { SelectQuota } from './SelectQuota';
import { PlanBillingSwitch } from './PlanBillingSwitch';
import {
  getTrueBundlePlan,
  getUniformCycleParam,
  KSE_STUDIO_PLAN_ROW,
  TRUE_MORE_TESTING_PRODUCT_FEATURES,
  TRUE_BUNDLE_PLAN_OPTIONS,
  TRUE_BUNDLE_PURCHASE_FEATURES,
} from './trueBundlePlans';
import { getManualPurchaseProductCode } from './trueBundleProductCode';
import {
  getMatchedPreviewProduct,
  getTrueBundlePreviewRequests,
} from './trueBundlePreview';

const useStyles = makeStyles(theme => ({
  sectionLabel: {
    fontWeight: 700,
    fontSize: 11,
    letterSpacing: 1,
    color: '#808B9A',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1),
  },
  planCard: {
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    marginBottom: theme.spacing(1.5),
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  selectedPlanCard: {
    borderColor: '#5959EB',
    boxShadow: '0 0 0 3px rgba(89, 89, 235, 0.08)',
  },
  planRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  planMain: {
    flex: 1,
    minWidth: 0,
  },
  planTitleRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
  },
  planTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: '#22283C',
  },
  planDescription: {
    color: '#6B7280',
    fontSize: 13,
    lineHeight: 1.45,
  },
  planPromoDescription: {
    color: '#6B7280',
    fontSize: 13,
    lineHeight: 1.45,
    marginTop: theme.spacing(0.75),
  },
  planPromoHighlight: {
    color: '#16865F',
    fontWeight: 800,
  },
  planProducts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginLeft: theme.spacing(1),
  },
  productIconBadge: {
    display: 'inline-flex',
    cursor: 'help',
  },
  planPrice: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'flex-end',
    gap: theme.spacing(0.5),
    minWidth: 136,
    textAlign: 'right',
    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-start',
      minWidth: 0,
      textAlign: 'left',
    },
  },
  billingCell: {
    display: 'flex',
    justifyContent: 'flex-end',
    flexShrink: 0,
    [theme.breakpoints.down('tablet')]: {
      justifyContent: 'flex-start',
    },
  },
  priceAmount: {
    color: '#22283C',
    fontWeight: 800,
    fontSize: 18,
  },
  priceHelp: {
    color: '#808B9A',
    fontSize: 12,
    whiteSpace: 'nowrap',
  },
  includedFooter: {
    backgroundColor: '#FAFAFB',
    border: '1px solid #E5E7EB',
    borderRadius: 8,
    padding: theme.spacing(1.5, 2),
  },
  includedTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  includedTitle: {
    color: '#808B9A',
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: 1,
    textTransform: 'uppercase',
  },
  includedItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.75),
  },
  includedCheckIcon: {
    color: '#12A877',
    fontSize: 18,
  },
  includedText: {
    color: '#6B7280',
    fontSize: 12,
    lineHeight: 1.5,
  },
  testingPlansSection: {
    marginTop: theme.spacing(3.5),
  },
  testingPlanRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.625, 2),
    border: '1px solid #ececf4',
    borderRadius: 12,
    backgroundColor: '#fff',
    marginTop: theme.spacing(1.25),
    [theme.breakpoints.down('tablet')]: {
      alignItems: 'flex-start',
      flexDirection: 'column',
    },
  },
  testingPlanMain: {
    flex: 1,
    minWidth: 0,
  },
  testingPlanTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  testingPlanTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#16141d',
  },
  testingPlanPrice: {
    fontSize: 13,
    fontWeight: 600,
    color: '#3c3a50',
    whiteSpace: 'nowrap',
    width: 72,
    flexShrink: 0,
    textAlign: 'right',
    [theme.breakpoints.down('tablet')]: {
      width: 'auto',
      textAlign: 'left',
    },
  },
  planInfoTooltip: {
    maxWidth: 300,
    padding: theme.spacing(1.75, 2),
    backgroundColor: '#16141d',
    color: '#d9d8e6',
    border: '1px solid #2c2940',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12.3,
    fontWeight: 400,
    lineHeight: 1.5,
    borderRadius: 12,
    boxShadow: '0 16px 40px rgba(16,20,45,.32)',
  },
  planInfoTooltipTitle: {
    color: '#8d8ba8',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.1em',
    textTransform: 'uppercase',
    marginBottom: theme.spacing(1.125),
  },
  tooltipList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    '& li': {
      position: 'relative',
      color: '#d9d8e6',
      fontSize: 12.5,
      lineHeight: 1.5,
      paddingLeft: theme.spacing(1.625),
      marginBottom: theme.spacing(1),
    },
    '& li::before': {
      content: '""',
      position: 'absolute',
      left: 0,
      top: 6,
      width: 5,
      height: 5,
      borderRadius: '50%',
      backgroundColor: '#19d89f',
    },
    '& li:last-child': {
      marginBottom: 0,
    },
  },
  productIconTooltip: {
    backgroundColor: '#16141d',
    color: '#ffffff',
    fontFamily: 'Inter, sans-serif',
    fontSize: 12,
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0.625, 1.125),
    borderRadius: 7,
    maxWidth: 'none',
  },
  // ---- Table (mock "Confirm your plan") layout ----
  tableCard: {
    border: '1px solid #e6e4ef',
    borderRadius: 16,
    overflow: 'visible',
    backgroundColor: '#fff',
  },
  tableHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 132px 132px 180px',
    columnGap: theme.spacing(1.75),
    alignItems: 'center',
    padding: theme.spacing(1.75, 3.25, 1.5),
    borderBottom: '1px solid #f1f0f7',
    [theme.breakpoints.down('tablet')]: {
      display: 'none',
    },
  },
  tableHeaderCell: {
    fontSize: 12,
    fontWeight: 500,
    color: '#6a6a6a',
  },
  tableHeaderCellEnd: {
    justifySelf: 'end',
  },
  tableGridRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 132px 132px 180px',
    columnGap: theme.spacing(1.75),
    alignItems: 'center',
    padding: theme.spacing(3, 3.25),
    [theme.breakpoints.down('tablet')]: {
      gridTemplateColumns: '1fr',
      rowGap: theme.spacing(1.5),
    },
  },
  tableRowDivider: {
    height: 1,
    backgroundColor: '#f1f0f7',
    margin: theme.spacing(0, 3.25),
  },
  planNameDashed: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 18,
    fontWeight: 400,
    color: '#16141d',
    borderBottom: '1.5px dashed #c9c7d9',
    paddingBottom: 2,
    cursor: 'help',
  },
  cellStart: {
    justifySelf: 'start',
    display: 'flex',
    alignItems: 'center',
  },
  cellSeats: {
    justifySelf: 'start',
  },
  addSeatButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    color: '#16141d',
    backgroundColor: '#fff',
    border: '1.5px solid #e6e4ef',
    borderRadius: 9,
    padding: theme.spacing(0.875, 1.75),
    fontSize: 12.5,
    fontWeight: 700,
    fontFamily: 'inherit',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#f3f2fb',
    },
  },
  priceCell: {
    justifySelf: 'end',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.75),
    whiteSpace: 'nowrap',
  },
  priceCellAmount: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 20,
    fontWeight: 500,
    letterSpacing: '-.01em',
    color: '#16141d',
  },
  includedNote: {
    padding: theme.spacing(1.625, 3.25, 1.875),
    fontSize: 13,
    lineHeight: 1.4,
    color: '#73718a',
    backgroundColor: '#fafafc',
    borderRadius: theme.spacing(0, 0, 2, 2),
  },
  includedNoteStrong: {
    color: '#16141d',
  },
  testingCard: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    border: '1px solid #ececf4',
    borderRadius: 16,
  },
  testingHeaderRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 132px 132px 180px',
    columnGap: theme.spacing(1.75),
    alignItems: 'center',
    padding: theme.spacing(2, 3.25, 1.5),
    borderBottom: '1px solid #f1f0f7',
    [theme.breakpoints.down('tablet')]: {
      display: 'none',
    },
  },
  testingHeaderLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: '.13em',
    textTransform: 'uppercase',
    color: '#0b6b4f',
  },
  testingNameDashed: {
    fontSize: 14,
    fontWeight: 500,
    color: '#16141d',
    borderBottom: '1.5px dashed #c9c7d9',
    paddingBottom: 2,
    cursor: 'help',
  },
  testingPriceAmount: {
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 15,
    fontWeight: 500,
    color: '#16141d',
    justifySelf: 'end',
    whiteSpace: 'nowrap',
  },
}));

const MAX_SEATS = process.env.REACT_APP_TRUE_TEAM_MAX_SEATS;
const TRUE_SUBSCRIPTION_FEATURES = [
  Feature.TRUE_TEAM,
  Feature.TRUE_ENTERPRISE,
  Feature.TRUE_PLATFORM_ONLY,
  Feature.TRUE_AUTOMATION,
];

type ProductIconKey = 'studio' | 'cloud' | 'local' | 'insights' | 'testmgmt';

const PRODUCT_ICON_META: Record<ProductIconKey, {
  backgroundColor: string;
  color: string;
}> = {
  studio: {
    backgroundColor: '#e3f6ee',
    color: '#12a877',
  },
  cloud: {
    backgroundColor: '#fdf3e1',
    color: '#bd862a',
  },
  local: {
    backgroundColor: '#ebecf7',
    color: '#5b5fb0',
  },
  insights: {
    backgroundColor: '#fce8f3',
    color: '#d6388a',
  },
  testmgmt: {
    backgroundColor: '#edeefb',
    color: '#7a7aef',
  },
};

const getPlanProductIconKey = (labelId: string): ProductIconKey => {
  if (labelId.includes('studio')) {
    return 'studio';
  }

  if (labelId.includes('cloud')) {
    return 'cloud';
  }

  return 'testmgmt';
};

const ProductGlyph = (
  { iconKey, color, backgroundColor }: {
    iconKey: ProductIconKey;
    color: string;
    backgroundColor: string;
  },
) => {
  const cutoutStrokeProps = {
    stroke: backgroundColor,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    strokeWidth: 1.8,
  };

  switch (iconKey) {
    case 'studio':
      return (
        <>
          <rect x="2.5" y="4" width="19" height="13" rx="2.6" fill={color} />
          <rect x="10" y="17" width="4" height="2.4" rx="0" fill={color} />
          <rect x="7.5" y="19.2" width="9" height="1.8" rx="0.9" fill={color} />
          <path d="M9 8 6.7 10.5 9 13" fill="none" {...cutoutStrokeProps} strokeWidth={1.7} />
          <path d="M15 8 17.3 10.5 15 13" fill="none" {...cutoutStrokeProps} strokeWidth={1.7} />
        </>
      );
    case 'cloud':
      return (
        <path
          d="M7 19h9.2a4.3 4.3 0 0 0 .55-8.56 5.8 5.8 0 0 0-11.06-1.16A4.1 4.1 0 0 0 7 19z"
          fill={color}
        />
      );
    case 'local':
      return (
        <>
          <rect x="2.5" y="5" width="19" height="14" rx="2.6" fill={color} />
          <path d="M6.5 10.5 9 13l-2.5 2.5" fill="none" {...cutoutStrokeProps} />
          <path d="M12 15.2h5" fill="none" {...cutoutStrokeProps} />
        </>
      );
    case 'insights':
      return (
        <>
          <rect x="4.2" y="12" width="3.4" height="6.5" rx="1" fill={color} />
          <rect x="10.3" y="7.5" width="3.4" height="11" rx="1" fill={color} />
          <rect x="16.4" y="9.8" width="3.4" height="8.7" rx="1" fill={color} />
        </>
      );
    case 'testmgmt':
    default:
      return (
        <>
          <rect x="5" y="4.5" width="14" height="16" rx="3" fill={color} />
          <rect x="8.8" y="3" width="6.4" height="3.4" rx="1.3" fill={color} />
          <path d="M8.6 12.6l2.2 2.2 4.6-5" fill="none" {...cutoutStrokeProps} strokeWidth={2.1} />
        </>
      );
  }
};

const ProductIconBadge = (
  {
    iconKey,
    size = 26,
    glyphSize = Math.round(size * 0.62),
    borderRadius = Math.round(size * 0.28),
  }: {
    iconKey: ProductIconKey;
    size?: number;
    glyphSize?: number;
    borderRadius?: number;
  },
) => {
  const meta = PRODUCT_ICON_META[iconKey];

  return (
    <Box
      component="span"
      sx={{
        alignItems: 'center',
        backgroundColor: meta.backgroundColor,
        // px string: numeric borderRadius in sx gets multiplied by theme.shape.borderRadius
        borderRadius: `${borderRadius}px`,
        display: 'inline-flex',
        flex: 'none',
        height: size,
        justifyContent: 'center',
        width: size,
      }}
    >
      <svg width={glyphSize} height={glyphSize} viewBox="0 0 24 24" fill="none" style={{ display: 'block' }}>
        <ProductGlyph
          iconKey={iconKey}
          color={meta.color}
          backgroundColor={meta.backgroundColor}
        />
      </svg>
    </Box>
  );
};

type TrueTestingPlanOption = {
  type: 'product';
  feature: Feature;
  titleId: string;
  infoId: string;
  iconKey: ProductIconKey;
};

// KSE is not listed here — it renders as a row of the main plan table
// (KSE_STUDIO_PLAN_ROW) while keeping its "more testing products" state/preview path.
const TRUE_TESTING_PLAN_OPTIONS: TrueTestingPlanOption[] = [
  {
    type: 'product',
    feature: Feature.UNLIMITED_ENGINE,
    titleId: 'feature.unlimited_engine.product.name',
    infoId: 'page.plan.v2.true.testing.runtime.info',
    iconKey: 'local',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_CROSS_BROWSER,
    titleId: 'page.plan.v2.true.testing.cross.browser.title',
    infoId: 'page.plan.v2.true.testing.cross.browser.info',
    iconKey: 'cloud',
  },
  {
    type: 'product',
    feature: Feature.TESTCLOUD_SESSION_MOBILE_APP,
    titleId: 'page.plan.v2.true.testing.mobile.title',
    infoId: 'page.plan.v2.true.testing.mobile.info',
    iconKey: 'cloud',
  },
];

type FeatureIntervals = Partial<Record<Feature, PlanInterval>>;

export const TrueBundlePlanSelect = () => {
  const classes = useStyles();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { flags } = useLaunchDarkly();
  const { get } = useQuery();
  const accountId = Number(get('accountId'));
  const moreTestingPlansEnabled = flags?.feTrueBundleMoreTestingPlansEnabled === true;
  const trackingCookies = useGetTrackingCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const automationSeatsParam = getQuotaFromPathParam(Feature.TRUE_AUTOMATION);
  const platformSeatsParam = getQuotaFromPathParam(Feature.TRUE_PLATFORM_ONLY);
  const studioSeatsParam = getQuotaFromPathParam(Feature.PER_USER_KSE);
  const runtimeSeatsParam = getQuotaFromPathParam(Feature.UNLIMITED_ENGINE);
  const crossBrowserSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_CROSS_BROWSER);
  const mobileAppSeatsParam = getQuotaFromPathParam(Feature.TESTCLOUD_SESSION_MOBILE_APP);
  // getCycleFromPathParam relies on useQuery internally, so it must be called at the
  // top level of the component (never inside effects/loops). Resolve every SKU's cycle
  // param once here and reuse the values below.
  const automationCycleParam = getCycleFromPathParam(Feature.TRUE_AUTOMATION);
  const platformCycleParam = getCycleFromPathParam(Feature.TRUE_PLATFORM_ONLY);
  const studioCycleParam = getCycleFromPathParam(Feature.PER_USER_KSE);
  const runtimeCycleParam = getCycleFromPathParam(Feature.UNLIMITED_ENGINE);
  const crossBrowserCycleParam = getCycleFromPathParam(Feature.TESTCLOUD_SESSION_CROSS_BROWSER);
  const mobileAppCycleParam = getCycleFromPathParam(Feature.TESTCLOUD_SESSION_MOBILE_APP);
  const cycleParamByFeature = useMemo<FeatureIntervals>(() => ({
    [Feature.TRUE_AUTOMATION]: automationCycleParam,
    [Feature.TRUE_PLATFORM_ONLY]: platformCycleParam,
    [Feature.PER_USER_KSE]: studioCycleParam,
    [Feature.UNLIMITED_ENGINE]: runtimeCycleParam,
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]: crossBrowserCycleParam,
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]: mobileAppCycleParam,
  }), [
    automationCycleParam,
    platformCycleParam,
    studioCycleParam,
    runtimeCycleParam,
    crossBrowserCycleParam,
    mobileAppCycleParam,
  ]);
  const targetSubscriptionId = parseSubscriptionId(get(PLAN_SUBSCRIPTION_ID_QUERY_PARAM));
  const selectAccountSubscriptions = useMemo(
    () => fromSubscription.selectByAccountId(accountId),
    [accountId],
  );
  const accountSubscriptions = useSelector(selectAccountSubscriptions);
  const activePromotion = useSelector(fromPromotion.selectActivePromotion);
  const orderPreviewRequest = useSelector(fromOrder.selectOrderPreviewRequest);
  const fallbackPreviewRequest = useSelector(fromOrder.selectTruePlatformPreviewRequest);
  const orderPreviewResponse = useSelector(fromOrder.selectOrderPreviewResponse);
  // Promotion the preview actually applied to the row's current selection.
  // Undefined while the preview for the current (productCode, quantity) pair
  // hasn't returned yet.
  const getAppliedPromo = (
    productCode: string,
    seats: number,
  ): { discountQuantity: number; perSeatDiscount: number } | undefined => {
    const request = getTrueBundlePreviewRequests(orderPreviewRequest, fallbackPreviewRequest)
      .find(req => req.productCode === productCode && req.quantity === seats);
    if (!request) {
      return undefined;
    }

    const matchedProduct = getMatchedPreviewProduct(request, orderPreviewResponse);
    if (!matchedProduct) {
      return undefined;
    }

    const discountQuantity = matchedProduct.appliedPromotion?.discountQuantity
      ?? matchedProduct.discountQuantity;
    const discountAmount = matchedProduct.appliedPromotion?.discountAmount
      ?? matchedProduct.discountAmount;
    return {
      discountQuantity,
      perSeatDiscount: discountQuantity > 0 ? discountAmount / discountQuantity : 0,
    };
  };
  // Per-unit discount of the active campaign for a product, used to show promo
  // prices before any quantity is selected (no preview exists yet).
  const getCampaignPerUnitDiscount = (productCode: string): number => {
    if (!activePromotion || !('discountedProducts' in activePromotion)) {
      return 0;
    }

    const discounted = activePromotion.discountedProducts.find(
      product => product.productCode === productCode,
    );
    if (!discounted || discounted.discountQuantity <= 0) {
      return 0;
    }

    return discounted.discountAmount / discounted.discountQuantity;
  };
  const lastRowPriceRef = useRef<Record<string, number>>({});
  // Unit price shown on a plan/product row. Mirrors the promotion the preview
  // actually applied: while every selected unit is covered by the promo quota,
  // show the per-unit promo price derived from the preview; once the selection
  // exceeds the quota (or no promo applies), show the standard unit price.
  // While the preview for the current quantity is still in flight, keep the
  // last shown price instead of flashing the base price until the response
  // lands. With no selection, fall back to the LD discount price so the row
  // matches the promo banner.
  const resolveRowUnitPrice = (
    feature: Feature,
    interval: PlanInterval,
    quantity: number,
    productCode: string,
  ): number => {
    const unitPrice = getUnitPrice(flags?.subscriptionPlanPrice, feature, interval, 'product');
    const discountPrice = getDiscountPrice(flags?.subscriptionPlanPrice, feature, interval);
    const hasDiscount = discountPrice > 0 && discountPrice < unitPrice;
    const fallbackPrice = hasDiscount ? discountPrice : unitPrice;
    const priceKey = `${feature}:${interval}`;

    if (quantity <= 0) {
      const campaignPerUnitDiscount = getCampaignPerUnitDiscount(productCode);
      const restingPrice = campaignPerUnitDiscount > 0 && campaignPerUnitDiscount < unitPrice
        ? unitPrice - campaignPerUnitDiscount
        : fallbackPrice;
      lastRowPriceRef.current[priceKey] = restingPrice;
      return restingPrice;
    }

    const appliedPromo = getAppliedPromo(productCode, quantity);
    if (appliedPromo === undefined) {
      return lastRowPriceRef.current[priceKey] ?? fallbackPrice;
    }

    const promoCoversSelection = appliedPromo.discountQuantity >= quantity
      && appliedPromo.perSeatDiscount > 0
      && appliedPromo.perSeatDiscount < unitPrice;
    const price = promoCoversSelection
      ? unitPrice - appliedPromo.perSeatDiscount
      : unitPrice;
    lastRowPriceRef.current[priceKey] = price;
    return price;
  };
  const trueSubscriptions = useMemo(
    () => accountSubscriptions.filter(
      sub => sub.source === SubscriptionSource.RECURLY
        && TRUE_SUBSCRIPTION_FEATURES.includes(sub.feature),
    ),
    [accountSubscriptions],
  );
  const recurlySubscriptions = useMemo(
    () => accountSubscriptions.filter(sub => sub.source === SubscriptionSource.RECURLY),
    [accountSubscriptions],
  );
  const selfServeBundleSubscriptions = useMemo(
    () => trueSubscriptions.filter(
      sub => isSelfServePaidSubscription(sub)
        && TRUE_BUNDLE_PURCHASE_FEATURES.includes(sub.feature),
    ),
    [trueSubscriptions],
  );
  const getBundleSubscriptionForFeature = useCallback(
    (feature: Feature) => getTargetSelfServeSubscription(
      selfServeBundleSubscriptions,
      feature,
      targetSubscriptionId,
    ),
    [selfServeBundleSubscriptions, targetSubscriptionId],
  );
  // A brand-new buyer (no paid Recurly subscription at all, self-serve or
  // direct-sale) has no existing interval to inherit, so the whole page follows
  // the cycle the incoming link asked for: when every cycle param in the URL
  // agrees, that cycle becomes the default for *all* rows, including the ones
  // the link never mentioned. Mixed monthly+annual params, or any paid
  // subscription on the account, fall back to the per-row default (annual).
  const hasPaidSubscription = useMemo(
    () => recurlySubscriptions.some(isPaidRecurlySubscription),
    [recurlySubscriptions],
  );
  const uniformCycleParam = useMemo(
    () => getUniformCycleParam(cycleParamByFeature),
    [cycleParamByFeature],
  );
  const defaultInterval = !hasPaidSubscription && uniformCycleParam !== undefined
    ? uniformCycleParam
    : PlanInterval.YEAR;
  const hasQuotaFromQuery = automationSeatsParam !== undefined || platformSeatsParam !== undefined;
  const syncedSubscriptionRef = useRef(false);
  const [planSeats, setPlanSeats] = useState<Partial<Record<Feature, number>>>({
    [Feature.TRUE_AUTOMATION]: automationSeatsParam ?? 0,
    [Feature.TRUE_PLATFORM_ONLY]: platformSeatsParam ?? 0,
  });
  const [testingProductSeats, setTestingProductSeats] = useState<Partial<Record<Feature, number>>>({
    [Feature.PER_USER_KSE]: studioSeatsParam ?? 0,
    [Feature.UNLIMITED_ENGINE]: runtimeSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]: crossBrowserSeatsParam ?? 0,
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]: mobileAppSeatsParam ?? 0,
  });
  // Initialise each SKU's interval from (1) the URL cycle param, then (2) the
  // existing self-serve subscription's interval, then (3) annual. Defaulting a
  // SKU that already has a monthly subscription straight to annual would make the
  // preview effect emit a spurious "monthly -> annual switch" request that then
  // leaks into the URL params and the confirmation page. Lazy init so it only
  // reads the subscriptions once.
  const resolveTestingInterval = (
    feature: Feature,
    cycleParam?: PlanInterval,
  ): PlanInterval => cycleParam
    ?? getTargetSelfServeSubscription(
      recurlySubscriptions,
      feature,
      targetSubscriptionId,
    )?.billingInterval
    ?? defaultInterval;
  const [planIntervals, setPlanIntervals] = useState<FeatureIntervals>(() => ({
    [Feature.TRUE_AUTOMATION]: automationCycleParam
      ?? getBundleSubscriptionForFeature(Feature.TRUE_AUTOMATION)?.billingInterval
      ?? defaultInterval,
    [Feature.TRUE_PLATFORM_ONLY]: platformCycleParam
      ?? getBundleSubscriptionForFeature(Feature.TRUE_PLATFORM_ONLY)?.billingInterval
      ?? defaultInterval,
  }));
  const [testingProductIntervals, setTestingProductIntervals] = useState<FeatureIntervals>(() => ({
    [Feature.PER_USER_KSE]: resolveTestingInterval(Feature.PER_USER_KSE, studioCycleParam),
    [Feature.UNLIMITED_ENGINE]: resolveTestingInterval(Feature.UNLIMITED_ENGINE, runtimeCycleParam),
    [Feature.TESTCLOUD_SESSION_CROSS_BROWSER]:
      resolveTestingInterval(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, crossBrowserCycleParam),
    [Feature.TESTCLOUD_SESSION_MOBILE_APP]:
      resolveTestingInterval(Feature.TESTCLOUD_SESSION_MOBILE_APP, mobileAppCycleParam),
  }));
  const intervalSyncedRef = useRef(false);
  const getPlanInterval = (feature: Feature): PlanInterval => planIntervals[feature]
    ?? PlanInterval.YEAR;
  const getTestingInterval = (feature: Feature): PlanInterval => testingProductIntervals[feature]
    ?? PlanInterval.YEAR;
  const getFeatureInterval = (feature: Feature): PlanInterval => (
    TRUE_BUNDLE_PURCHASE_FEATURES.includes(feature)
      ? getPlanInterval(feature)
      : getTestingInterval(feature)
  );
  const hasAnnualPaidSubForFeature = (feature: Feature): boolean => recurlySubscriptions.some(
    sub => sub.feature === feature && sub.billingInterval === PlanInterval.YEAR,
  );

  useEffect(() => {
    if (!activePromotion) {
      dispatch(fromPromotion.doFetchActivePromotion());
    }
  }, [activePromotion, dispatch]);

  useEffect(() => {
    setPlanSeats(current => {
      let next = current;
      const syncQuota = (feature: Feature, quota: number | undefined) => {
        if (quota === undefined || current[feature] === quota) {
          return;
        }
        if (next === current) {
          next = { ...current };
        }
        next[feature] = quota;
      };

      syncQuota(Feature.TRUE_AUTOMATION, automationSeatsParam);
      syncQuota(Feature.TRUE_PLATFORM_ONLY, platformSeatsParam);
      return next;
    });
  }, [automationSeatsParam, platformSeatsParam]);

  useEffect(() => {
    setTestingProductSeats(current => {
      let next = current;
      const syncQuota = (feature: Feature, quota: number | undefined) => {
        if (quota === undefined || current[feature] === quota) {
          return;
        }
        if (next === current) {
          next = { ...current };
        }
        next[feature] = quota;
      };

      syncQuota(Feature.PER_USER_KSE, studioSeatsParam);
      syncQuota(Feature.UNLIMITED_ENGINE, runtimeSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_CROSS_BROWSER, crossBrowserSeatsParam);
      syncQuota(Feature.TESTCLOUD_SESSION_MOBILE_APP, mobileAppSeatsParam);
      return next;
    });
  }, [
    studioSeatsParam,
    runtimeSeatsParam,
    crossBrowserSeatsParam,
    mobileAppSeatsParam,
  ]);

  const getTrackingPayloadForFeature = (
    feature: Feature,
    quantity?: number,
    intervalOverride?: PlanInterval,
  ): TrackingPayload => {
    const interval = intervalOverride ?? getFeatureInterval(feature);
    return {
      object: Feature.toString(feature),
      product_id: getProductId(feature),
      product_code: getManualPurchaseProductCode(feature, interval),
      billing_interval: PlanInterval.getPlanIntervalCode(interval),
      unit_price: getUnitPrice(
        flags?.subscriptionPlanPrice,
        feature,
        interval,
        'product',
      ),
      ...(quantity !== undefined ? { quantity } : {}),
    };
  };

  const trackPlanPageAction = (payload: TrackingPayload) => {
    if (!accountId) {
      return;
    }

    sendTrackingData(
      'plan_page_all_action',
      accountId,
      undefined,
      {
        field: payload.field,
        object: payload.object,
        action: payload.action,
        plan_flow: 'true_bundle',
        ...payload,
        ...trackingCookies,
        cart_snapshot: cartSnapshot,
      },
    );
  };

  useEffect(() => {
    if (selfServeBundleSubscriptions.length === 0
      || hasQuotaFromQuery
      || syncedSubscriptionRef.current) {
      return;
    }

    syncedSubscriptionRef.current = true;
    setPlanSeats({
      [Feature.TRUE_AUTOMATION]: 0,
      [Feature.TRUE_PLATFORM_ONLY]: 0,
    });
  }, [hasQuotaFromQuery, selfServeBundleSubscriptions]);

  // Default each SKU's billing interval to its existing self-serve subscription
  // interval (unless the interval is pinned by a URL param). Runs once, after the
  // account subscriptions have loaded. Rows without their own subscription fall
  // back to `defaultInterval` rather than keeping the lazy-init value: the state
  // is initialised on the first render, when the store may still be empty and
  // `defaultInterval` therefore still the uniform-cycle one. Re-resolving here
  // corrects those rows once a paid subscription turns up.
  useEffect(() => {
    if (intervalSyncedRef.current
      || (recurlySubscriptions.length === 0 && selfServeBundleSubscriptions.length === 0)) {
      return;
    }
    intervalSyncedRef.current = true;

    const planUpdates: FeatureIntervals = {};
    TRUE_BUNDLE_PLAN_OPTIONS.forEach(plan => {
      if (cycleParamByFeature[plan.feature] !== undefined) {
        return;
      }
      const planSubscription = getBundleSubscriptionForFeature(plan.feature);
      planUpdates[plan.feature] = planSubscription?.billingInterval ?? defaultInterval;
    });
    setPlanIntervals(current => ({ ...current, ...planUpdates }));

    const testingUpdates: FeatureIntervals = {};
    TRUE_MORE_TESTING_PRODUCT_FEATURES.forEach(feature => {
      if (cycleParamByFeature[feature] !== undefined) {
        return;
      }
      const productSubscription = getTargetSelfServeSubscription(
        recurlySubscriptions,
        feature,
        targetSubscriptionId,
      );
      testingUpdates[feature] = productSubscription?.billingInterval ?? defaultInterval;
    });
    setTestingProductIntervals(current => ({ ...current, ...testingUpdates }));
  }, [
    cycleParamByFeature,
    defaultInterval,
    getBundleSubscriptionForFeature,
    recurlySubscriptions,
    selfServeBundleSubscriptions,
    targetSubscriptionId,
  ]);

  useEffect(() => {
    TRUE_BUNDLE_PLAN_OPTIONS.forEach(plan => {
      const interval = getPlanInterval(plan.feature);
      const planSubscription = getBundleSubscriptionForFeature(plan.feature);
      const isMonthlyToAnnualSwitch = !!planSubscription?.recurlySubUuId
        && planSubscription.billingInterval === PlanInterval.MONTH
        && interval === PlanInterval.YEAR
        && (planSeats[plan.feature] ?? 0) === 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: getManualPurchaseProductCode(plan.feature, interval),
          quantity: planSeats[plan.feature] ?? 0,
          ...(planSubscription?.recurlySubUuId
            ? { recurlySubscriptionUuid: planSubscription.recurlySubUuId }
            : {}),
        },
        keepZeroQuantity: isMonthlyToAnnualSwitch,
      }));
    });

    const bundleSubscription = targetSubscriptionId
      ? selfServeBundleSubscriptions.find(sub => sub.id === targetSubscriptionId)
      : selfServeBundleSubscriptions[0];
    if (bundleSubscription) {
      const subscribedPlan = TRUE_BUNDLE_PLAN_OPTIONS.find(
        plan => plan.feature === bundleSubscription.feature,
      );
      if (subscribedPlan) {
        dispatch(fromOrder.setTruePlatformPreviewRequest({
          productCode: getManualPurchaseProductCode(
            subscribedPlan.feature,
            getPlanInterval(subscribedPlan.feature),
          ),
          quantity: planSeats[bundleSubscription.feature] ?? 0,
          ...(bundleSubscription.recurlySubUuId
            ? { recurlySubscriptionUuid: bundleSubscription.recurlySubUuId }
            : {}),
        }));
      }
    }
  }, [
    planIntervals,
    dispatch,
    getBundleSubscriptionForFeature,
    planSeats,
    selfServeBundleSubscriptions,
    targetSubscriptionId,
  ]);

  useEffect(() => {
    TRUE_MORE_TESTING_PRODUCT_FEATURES.forEach(feature => {
      const interval = getTestingInterval(feature);
      const productSubscription = getTargetSelfServeSubscription(
        recurlySubscriptions,
        feature,
        targetSubscriptionId,
      );
      const quantity = moreTestingPlansEnabled ? testingProductSeats[feature] ?? 0 : 0;
      // Switching an existing monthly subscription to annual without adding seats still needs a
      // preview (the plan change is billable). Keep the zero-quantity product in the request so the
      // preview fires, mirroring the bundle-plan effect above.
      const isMonthlyToAnnualSwitch = !!productSubscription?.recurlySubUuId
        && productSubscription.billingInterval === PlanInterval.MONTH
        && interval === PlanInterval.YEAR
        && quantity === 0;
      dispatch(fromOrder.updateProductPreviewRequest({
        product: {
          productCode: `${getProductId(feature)}_${PlanInterval.getPlanIntervalCode(interval)}`,
          quantity,
          ...(productSubscription?.recurlySubUuId
            ? { recurlySubscriptionUuid: productSubscription.recurlySubUuId }
            : {}),
        },
        keepZeroQuantity: isMonthlyToAnnualSwitch,
      }));
    });
  }, [
    testingProductIntervals,
    dispatch,
    moreTestingPlansEnabled,
    recurlySubscriptions,
    targetSubscriptionId,
    testingProductSeats,
  ]);

  const updatePlanSeats = (feature: Feature) => (quota: number) => {
    const previousQuota = planSeats[feature] ?? 0;
    if (previousQuota !== quota) {
      trackPlanPageAction({
        field: 'true_bundle_plan',
        action: 'change_quantity',
        previous_quantity: previousQuota,
        ...getTrackingPayloadForFeature(feature, quota),
      });
    }

    setPlanSeats(current => ({
      ...current,
      [feature]: quota,
    }));
  };

  const updateTestingProductSeats = (feature: Feature) => (quota: number) => {
    const previousQuota = testingProductSeats[feature] ?? 0;
    if (previousQuota !== quota) {
      trackPlanPageAction({
        field: 'more_testing_plan',
        action: 'change_quantity',
        previous_quantity: previousQuota,
        ...getTrackingPayloadForFeature(feature, quota),
      });
    }

    setTestingProductSeats(current => ({
      ...current,
      [feature]: quota,
    }));
  };

  const updatePlanInterval = (feature: Feature) => (interval: PlanInterval) => {
    if (getPlanInterval(feature) === interval) {
      return;
    }
    trackPlanPageAction({
      field: 'billing_interval',
      action: 'change_interval',
      ...getTrackingPayloadForFeature(feature, planSeats[feature] ?? 0, interval),
    });
    setPlanIntervals(current => ({
      ...current,
      [feature]: interval,
    }));
  };

  const updateTestingProductInterval = (feature: Feature) => (interval: PlanInterval) => {
    if (getTestingInterval(feature) === interval) {
      return;
    }
    trackPlanPageAction({
      field: 'billing_interval',
      action: 'change_interval',
      ...getTrackingPayloadForFeature(feature, testingProductSeats[feature] ?? 0, interval),
    });
    setTestingProductIntervals(current => ({
      ...current,
      [feature]: interval,
    }));
  };

  const renderAddButton = (onAdd: () => void) => (
    <Box
      component="button"
      type="button"
      onClick={onAdd}
      className={classes.addSeatButton}
    >
      +
      {' '}
      <FormattedMessage id="page.plan.v2.true.bundle.add" />
    </Box>
  );

  // Display order: True Automation, then True Platform, then Katalon Studio
  // Enterprise. KSE keeps its "more testing products" state/preview path, so it
  // stays gated behind the same LD flag as that section.
  const planTableRows = [
    getTrueBundlePlan(Feature.TRUE_AUTOMATION),
    getTrueBundlePlan(Feature.TRUE_PLATFORM_ONLY),
    ...(moreTestingPlansEnabled ? [KSE_STUDIO_PLAN_ROW] : []),
  ];

  return (
    <Box id="true-bundle-plan-select" pb={2}>
      {/* ---- Plan table ---- */}
      <Box className={classes.tableCard}>
        <Box className={classes.tableHeaderRow}>
          <Typography className={classes.tableHeaderCell}>
            <FormattedMessage id="page.plan.v2.table.header.plan" />
          </Typography>
          <Typography className={classes.tableHeaderCell}>
            <FormattedMessage id="page.plan.v2.billing.cycle" />
          </Typography>
          <Typography className={classes.tableHeaderCell}>
            <FormattedMessage id="page.plan.v2.table.header.seats" />
          </Typography>
          <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellEnd}`}>
            <FormattedMessage id="page.plan.v2.table.header.price" />
          </Typography>
        </Box>

        {planTableRows.map((plan, index) => {
          // KSE renders in the plan table but keeps its "more testing products"
          // seats/interval state and preview product-code path.
          const isTestingProduct = plan.feature === Feature.PER_USER_KSE;
          const seats = (isTestingProduct ? testingProductSeats : planSeats)[plan.feature] ?? 0;
          const interval = getFeatureInterval(plan.feature);
          const updateSeats = isTestingProduct
            ? updateTestingProductSeats(plan.feature)
            : updatePlanSeats(plan.feature);
          const updateInterval = isTestingProduct
            ? updateTestingProductInterval(plan.feature)
            : updatePlanInterval(plan.feature);
          const trackingField = isTestingProduct ? 'more_testing_plan' : 'true_bundle_plan';
          const rowProductCode = isTestingProduct
            ? `${getProductId(plan.feature)}_${PlanInterval.getPlanIntervalCode(interval)}`
            : getManualPurchaseProductCode(plan.feature, interval);
          const effectivePrice = resolveRowUnitPrice(plan.feature, interval, seats, rowProductCode);

          return (
            <Box key={plan.feature}>
              {index > 0 && <Box className={classes.tableRowDivider} />}
              <Box className={classes.tableGridRow}>
                {/* Plan */}
                <Box className={classes.planMain}>
                  <Box className={classes.planTitleRow}>
                    <Tooltip
                      disableInteractive
                      placement="bottom-start"
                      onOpen={() => trackPlanPageAction({
                        field: trackingField,
                        action: 'view_tooltip',
                        tooltip: 'plan_details',
                        ...getTrackingPayloadForFeature(plan.feature, seats),
                      })}
                      classes={{
                        tooltip: classes.planInfoTooltip,
                      }}
                      title={(
                        <Box>
                          <Typography className={classes.planInfoTooltipTitle}>
                            {intl.formatMessage({ id: plan.tooltipTitleId })}
                          </Typography>
                          <ul className={classes.tooltipList}>
                            {plan.benefitIds.map(benefitId => (
                              <li key={benefitId}>{intl.formatMessage({ id: benefitId })}</li>
                            ))}
                          </ul>
                        </Box>
                      )}
                    >
                      <Typography component="span" className={classes.planNameDashed}>
                        <FormattedMessage id={plan.titleId} />
                      </Typography>
                    </Tooltip>
                  </Box>
                  <Box className={classes.planProducts} sx={{ ml: 0, mt: 1 }}>
                    {plan.productLabelIds.map(labelId => {
                      const productIconKey = getPlanProductIconKey(labelId);
                      const productLabel = intl.formatMessage({ id: labelId });

                      return (
                        <Tooltip
                          disableInteractive
                          key={labelId}
                          title={productLabel}
                          placement="top-start"
                          classes={{
                            tooltip: classes.productIconTooltip,
                          }}
                          onOpen={() => trackPlanPageAction({
                            field: 'true_bundle_product_icon',
                            object: productLabel,
                            action: 'view_tooltip',
                            parent_object: Feature.toString(plan.feature),
                            label_id: labelId,
                            billing_interval: PlanInterval.getPlanIntervalCode(interval),
                          })}
                        >
                          <Box
                            aria-label={productLabel}
                            className={classes.productIconBadge}
                            component="span"
                            role="img"
                          >
                            <ProductIconBadge iconKey={productIconKey} />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>

                {/* Billing cycle */}
                <Box className={classes.cellStart}>
                  <PlanBillingSwitch
                    interval={interval}
                    onChange={updateInterval}
                    disabled={hasAnnualPaidSubForFeature(plan.feature)}
                  />
                </Box>

                {/* Add seats */}
                <Box className={classes.cellSeats}>
                  {seats === 0 ? renderAddButton(() => updateSeats(1)) : (
                    <SelectQuota
                      productId={getProductId(plan.feature)}
                      initQuota={0}
                      quota={seats}
                      setQuota={updateSeats}
                      maxQuota={!isTestingProduct && MAX_SEATS ? Number(MAX_SEATS) : undefined}
                      width={30}
                      height={32}
                    />
                  )}
                </Box>

                {/* Price / seat */}
                <Box className={classes.priceCell}>
                  <Typography component="span" className={classes.priceCellAmount}>
                    {formatCurrency(intl, effectivePrice, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          );
        })}

        <Box className={classes.includedNote}>
          <Box component="span" className={classes.includedNoteStrong}>
            <FormattedMessage id="page.plan.v2.true.bundle.included.title" />
            {': '}
          </Box>
          <FormattedMessage id="page.plan.v2.true.bundle.included.description" />
        </Box>
      </Box>

      {moreTestingPlansEnabled && (
        <Box className={classes.testingPlansSection}>
          <Box className={classes.testingCard}>
            <Box className={classes.testingHeaderRow}>
              <Typography className={classes.testingHeaderLabel}>
                <FormattedMessage id="page.plan.v2.true.bundle.more.testing.plans" />
              </Typography>
              <Typography className={classes.tableHeaderCell}>
                <FormattedMessage id="page.plan.v2.billing.cycle" />
              </Typography>
              <Typography className={classes.tableHeaderCell}>
                <FormattedMessage id="page.plan.v2.table.header.quantity" />
              </Typography>
              <Typography className={`${classes.tableHeaderCell} ${classes.tableHeaderCellEnd}`}>
                <FormattedMessage id="page.plan.v2.table.header.price.short" />
              </Typography>
            </Box>

            {TRUE_TESTING_PLAN_OPTIONS.map((option, index) => {
              const quota = testingProductSeats[option.feature] ?? 0;
              const interval = getTestingInterval(option.feature);
              const effectivePrice = resolveRowUnitPrice(
                option.feature,
                interval,
                quota,
                `${getProductId(option.feature)}_${PlanInterval.getPlanIntervalCode(interval)}`,
              );

              return (
                <Box key={option.feature}>
                  {index > 0 && <Box className={classes.tableRowDivider} />}
                  <Box className={classes.tableGridRow} sx={{ py: 2.25 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.375, minWidth: 0 }}>
                      <ProductIconBadge
                        iconKey={option.iconKey}
                        size={28}
                        glyphSize={17}
                        borderRadius={8}
                      />
                      <Tooltip
                        disableInteractive
                        title={intl.formatMessage({ id: option.infoId })}
                        placement="bottom-start"
                        classes={{
                          tooltip: classes.planInfoTooltip,
                        }}
                        onOpen={() => trackPlanPageAction({
                          field: 'more_testing_plan',
                          action: 'view_tooltip',
                          tooltip: 'plan_details',
                          ...getTrackingPayloadForFeature(option.feature, quota),
                        })}
                      >
                        <Typography component="span" className={classes.testingNameDashed}>
                          <FormattedMessage id={option.titleId} />
                        </Typography>
                      </Tooltip>
                    </Box>

                    <Box className={classes.cellStart}>
                      <PlanBillingSwitch
                        interval={interval}
                        onChange={updateTestingProductInterval(option.feature)}
                        disabled={hasAnnualPaidSubForFeature(option.feature)}
                      />
                    </Box>

                    <Box className={classes.cellSeats}>
                      {quota === 0
                        ? renderAddButton(() => updateTestingProductSeats(option.feature)(1))
                        : (
                          <SelectQuota
                            productId={getProductId(option.feature)}
                            initQuota={0}
                            quota={quota}
                            setQuota={updateTestingProductSeats(option.feature)}
                            width={30}
                            height={32}
                          />
                        )}
                    </Box>

                    <Typography className={classes.testingPriceAmount}>
                      {formatCurrency(intl, effectivePrice, DEFAULT_CURRENCY_FORMAT_OPTIONS)}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}
    </Box>
  );
};
